import axios from "axios";
import { AppID, URL } from "../../constants/provider/constant";
import { axiosWithRetry } from "../../helper/API";
import { toast } from "react-toast";
import { uploadFile } from "../centralOms/common/apiCalls";
import { WAPIGetFileURL } from "./constants";

// Axios instance specifically for Vision API calls
const visionApi = axios.create({
  baseURL: URL.vision_url,
  headers: {
    Accept: "application/json",
    "x-mb-source": "provider-central",
  },
});

// Request interceptor to add the auth token to every request
visionApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token_vision");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle 403 errors and token refresh
visionApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried refreshing the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await generateToken();
        // Retry the original request with new token
        return visionApi(originalRequest);
      } catch (refreshError) {
        toast.error("Token generation failed", "error");
        return Promise.reject(refreshError);
      }
    }

    // For other types of errors
    toast.error("Failed to fetch results. Please try again", "error");
    return Promise.reject(error);
  }
);

export const generateToken = async () => {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const userDetails = JSON.parse(localStorage.getItem("user_details"));
      let data = {
        entityId: userDetails.entityId,
      };

      let config = {
        method: "post",
        url: URL.providercore_url + "vision/generate-token",
        headers: {
          accept: "application/json",
        },
        data: data,
      };

      let response = await axiosWithRetry(config);
      if (response.data?.data?.token) {
        localStorage.setItem("access_token_vision", response.data.data.token);
      }
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
};

export const validateOrderDetails = async (orderId) => {
  try {
    const response = await visionApi.get(
      `api/vision/validateorderdetails?orderId=${orderId}`,
      {
        headers: {
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to validate order details:", error?.message);
    throw error;
  }
};

export const verifypayment = async (orderId) => {
  try {
    const response = await visionApi.post(
      `api/vision/verifypayment`,
      {
        orderId: orderId,
      },
      {
        headers: {
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to validate order details:", error?.message);
    throw error;
  }
};

export const fetchAmountSplit = async (body) => {
  try {
    const response = await visionApi.post("api/vision/fetchamountsplit", body, {
      headers: {
        "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch amount split:", error?.message);
    throw error;
  }
};

export const requestPayment = async (body, otpId) => {
  try {
    const response = await visionApi.post(
      "api/vision/confirmpayment",
      body,
      {
        headers: {
          "x-mb-otp-id": otpId,
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to request payment:", error?.message);
    throw error;
  }
};

export const orderUpdateV1 = async (body, otpId) => {
  try {
    const response = await visionApi.post(
      "api/vision/v1/webhook/orderupdate",
      body,
      {
        headers: {
          "x-mb-otp-id": otpId,
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update order:", error?.message);
    throw error;
  }
};

export const orderUpdate = async (body) => {
  try {
    const response = await visionApi.post(
      "api/vision/webhook/orderupdate",
      body,
      {
        headers: {
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update order:", error?.message);
    throw error;
  }
};

export const getItemDetails = async (requestId) => {
  try {
    const response = await axios.get(
      `${URL.providercore_url}vision/item-details?requestId=${requestId}`,
      {
        headers: {
          accesstoken: localStorage.getItem("access_token"),
        },
      }
    );
    if (response.data.success) {
      const items = [];
      response.data.response.forEach((item) => {
        items.push({
          itemId: item.itemid,
          itemName: item.itemname,
          category: item.productcategory,
          lensType: item.lenstype,
          quantity: 1,
          totalFramePrice: parseFloat(item.framemrp || 0),
          totalFrameDiscount: parseFloat(item.framediscount || 0),
          totalLensPrice: parseFloat(item.lensmrp || 0),
          totalLensDiscount: parseFloat(item.lensdiscount || 0),
          totalOtherPrice: parseFloat(item.othermrp || 0),
          totalOtherDiscount: parseFloat(item.otherdiscount || 0),
          totalPrice: item.itemmrp,
          totalDiscount: item.directdiscount,
        });
      });
      return items;
    } else {
      toast.error("Failed to fetch item details");
    }
    return [];
  } catch (error) {
    toast.error("Failed to fetch item details");
    return [];
  }
};

export const getItemDetailsForOrderUpdate = async (requestId) => {
  try {
    const response = await axios.get(
      `${URL.providercore_url}vision/item-details?requestId=${requestId}`,
      {
        headers: {
          accesstoken: localStorage.getItem("access_token"),
        },
      }
    );
    if (response.data.success) {
      const items = [];
      response.data.response.forEach((item) => {
        items.push({
          itemId: item.itemid,
          itemName: item.itemname,
          category: item.productcategory,
          lensType: item.lenstype,
          quantityOrdered: 1,
          quantityDelivered: 1,
          quantityInProgress: 0,
        });
      });
      return items;
    } else {
      toast.error("Failed to fetch item details");
    }
    return [];
  } catch (error) {
    toast.error("Failed to fetch item details");
    return [];
  }
};

export const uploadFilesAPI = async (
  uploadedFiles,
  contractId,
  key = "url"
) => {
  try {
    const guids = [];
    for (const file of uploadedFiles) {
      const payload = new FormData();

      payload.append("fileName", file.name);
      payload.append("fileType", "Prescription");
      payload.append("file", file);
      payload.append("appId", AppID);
      payload.append("contractType", contractId);
      payload.append("s3AccountType", "MB");

      let response = await uploadFile(payload);
      if (response?.success && response?.data) {
        guids.push({
          [key]: `${WAPIGetFileURL}/${Object.keys(response.data)[0]}`,
          type: file.type.split("/")[1],
        });
      }
    }
    return guids;
  } catch (error) {
    console.error("Failed to upload prescriptions:", error?.message);
    throw error;
  }
};

export const generateOTP = async (orderId, otpType) => {
  try {
    const response = await visionApi.post(
      "vision-care/auth/generate-otp",
      {
        orderId: parseInt(orderId),
        otpType: otpType,
      },
      {
        headers: {
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to generate OTP:", error?.message);
    throw error;
  }
};

export const verifyOTP = async (otpId, otp) => {
  try {
    const response = await visionApi.post(
      "vision-care/auth/verify-otp",
      {
        otp: parseInt(otp),
      },
      {
        headers: {
          "x-mb-otp-id": otpId,
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to verify OTP:", error?.message);
    throw error;
  }
};

export const resentOTP = async (otpId) => {
  try {
    const response = await visionApi.put(
      "vision-care/auth/resend-otp",
      {},
      {
        headers: {
          "x-mb-otp-id": otpId,
          "mb-access-token": JSON.parse(localStorage.getItem("access_token")),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to resend OTP:", error?.message);
    throw error;
  }
};
